"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useStore } from "@/lib/store";
import { createClient } from "@/lib/supabase/client";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Pen,
  Square,
  ArrowRight as ArrowIcon,
  Type as TypeIcon,
  Undo2,
  Save,
  Loader2,
} from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Click an inline image in a Pauta markdown body → this modal opens with
 * a canvas overlay where you can draw arrows, boxes, freehand, and text
 * to call out something for a guide / report. Save → uploads a new PNG
 * to the `attachments` bucket and the parent (DocsView / IssueDetail)
 * replaces the original URL with the annotated one in markdown source.
 *
 * MVP scope (PAUT-67):
 * - 4 tools: pen / arrow / box / text
 * - 5 colors (red / yellow / green / blue / accent) + 3 stroke widths
 * - undo (last shape)
 * - save → POST to attachments bucket
 * - cancel
 *
 * Out of scope: redo, layer reorder, crop, blur, shape drag-after-place.
 */

type Color = string;
type StrokeWidth = 2 | 4 | 6;
type Tool = "pen" | "arrow" | "box" | "text";

type Point = { x: number; y: number };

type Shape =
  | { kind: "pen"; color: Color; width: StrokeWidth; points: Point[] }
  | { kind: "arrow"; color: Color; width: StrokeWidth; from: Point; to: Point }
  | { kind: "box"; color: Color; width: StrokeWidth; from: Point; to: Point }
  | { kind: "text"; color: Color; size: number; at: Point; text: string };

const COLORS: Color[] = [
  "var(--red)",
  "var(--orange)",
  "var(--green)",
  "var(--blue)",
  "var(--accent)",
];

export function ImageAnnotator({
  open,
  imageUrl,
  onCancel,
  onSave,
}: {
  open: boolean;
  imageUrl: string;
  onCancel: () => void;
  /** Called with the new attachment URL after upload succeeds. */
  onSave: (newUrl: string) => void;
}) {
  const activeWorkspaceId = useStore((s) => s.activeWorkspaceId);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [tool, setTool] = useState<Tool>("arrow");
  const [color, setColor] = useState<Color>("var(--red)");
  const [width, setWidth] = useState<StrokeWidth>(4);
  const [shapes, setShapes] = useState<Shape[]>([]);
  const [drawing, setDrawing] = useState<Shape | null>(null);
  const [textInput, setTextInput] = useState<{ at: Point; value: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [imageReady, setImageReady] = useState(false);

  // Load the image once when the modal opens.
  useEffect(() => {
    if (!open) {
      setShapes([]);
      setDrawing(null);
      setTextInput(null);
      setImageReady(false);
      return;
    }
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      imgRef.current = img;
      const canvas = canvasRef.current;
      if (canvas) {
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
      }
      setImageReady(true);
    };
    img.onerror = () => {
      console.error("[annotator] failed to load image", imageUrl);
    };
    img.src = imageUrl;
  }, [open, imageUrl]);

  // Re-render the canvas whenever shapes / drawing change.
  useEffect(() => {
    if (!imageReady) return;
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0);
    for (const s of shapes) drawShape(ctx, s);
    if (drawing) drawShape(ctx, drawing);
  }, [shapes, drawing, imageReady]);

  function resolveColor(c: Color): string {
    if (c.startsWith("var(")) {
      // Resolve at draw time so we get the live theme value.
      const root = getComputedStyle(document.documentElement);
      const name = c.match(/var\((--[^)]+)\)/)?.[1];
      if (name) return root.getPropertyValue(name).trim() || "#eb5757";
    }
    return c;
  }

  function drawShape(ctx: CanvasRenderingContext2D, s: Shape) {
    const stroke = resolveColor(s.color);
    if (s.kind === "pen") {
      ctx.strokeStyle = stroke;
      ctx.lineWidth = s.width;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.beginPath();
      s.points.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
      ctx.stroke();
    } else if (s.kind === "arrow") {
      ctx.strokeStyle = stroke;
      ctx.fillStyle = stroke;
      ctx.lineWidth = s.width;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(s.from.x, s.from.y);
      ctx.lineTo(s.to.x, s.to.y);
      ctx.stroke();
      const angle = Math.atan2(s.to.y - s.from.y, s.to.x - s.from.x);
      const headLen = 8 + s.width * 2;
      ctx.beginPath();
      ctx.moveTo(s.to.x, s.to.y);
      ctx.lineTo(
        s.to.x - headLen * Math.cos(angle - Math.PI / 6),
        s.to.y - headLen * Math.sin(angle - Math.PI / 6)
      );
      ctx.lineTo(
        s.to.x - headLen * Math.cos(angle + Math.PI / 6),
        s.to.y - headLen * Math.sin(angle + Math.PI / 6)
      );
      ctx.closePath();
      ctx.fill();
    } else if (s.kind === "box") {
      ctx.strokeStyle = stroke;
      ctx.lineWidth = s.width;
      ctx.strokeRect(
        Math.min(s.from.x, s.to.x),
        Math.min(s.from.y, s.to.y),
        Math.abs(s.to.x - s.from.x),
        Math.abs(s.to.y - s.from.y)
      );
    } else if (s.kind === "text") {
      ctx.fillStyle = stroke;
      ctx.font = `bold ${s.size}px Inter, system-ui, sans-serif`;
      ctx.textBaseline = "top";
      ctx.fillText(s.text, s.at.x, s.at.y);
    }
  }

  function pointFromEvent(e: React.PointerEvent<HTMLCanvasElement>): Point {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const sx = canvas.width / rect.width;
    const sy = canvas.height / rect.height;
    return {
      x: (e.clientX - rect.left) * sx,
      y: (e.clientY - rect.top) * sy,
    };
  }

  function onPointerDown(e: React.PointerEvent<HTMLCanvasElement>) {
    if (textInput) return;
    const p = pointFromEvent(e);
    if (tool === "text") {
      setTextInput({ at: p, value: "" });
      return;
    }
    if (tool === "pen") {
      setDrawing({ kind: "pen", color, width, points: [p] });
    } else if (tool === "arrow") {
      setDrawing({ kind: "arrow", color, width, from: p, to: p });
    } else if (tool === "box") {
      setDrawing({ kind: "box", color, width, from: p, to: p });
    }
    canvasRef.current?.setPointerCapture(e.pointerId);
  }

  function onPointerMove(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawing) return;
    const p = pointFromEvent(e);
    if (drawing.kind === "pen") {
      setDrawing({ ...drawing, points: [...drawing.points, p] });
    } else if (drawing.kind === "arrow" || drawing.kind === "box") {
      setDrawing({ ...drawing, to: p });
    }
  }

  function onPointerUp(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawing) return;
    canvasRef.current?.releasePointerCapture(e.pointerId);
    setShapes((s) => [...s, drawing]);
    setDrawing(null);
  }

  function commitText() {
    if (!textInput || !textInput.value.trim()) {
      setTextInput(null);
      return;
    }
    const size = Math.max(16, width * 6);
    setShapes((s) => [
      ...s,
      { kind: "text", color, size, at: textInput.at, text: textInput.value.trim() },
    ]);
    setTextInput(null);
  }

  function undo() {
    setShapes((s) => s.slice(0, -1));
  }

  async function save() {
    const canvas = canvasRef.current;
    if (!canvas || !activeWorkspaceId) return;
    setSaving(true);
    try {
      const blob: Blob | null = await new Promise((resolve) =>
        canvas.toBlob((b) => resolve(b), "image/png", 0.92)
      );
      if (!blob) {
        console.error("[annotator] toBlob returned null");
        return;
      }
      const sb = createClient();
      const path = `${activeWorkspaceId}/${crypto.randomUUID()}.png`;
      const { error } = await sb.storage.from("attachments").upload(path, blob, {
        cacheControl: "3600",
        upsert: false,
        contentType: "image/png",
      });
      if (error) {
        console.error("[annotator] upload failed", error);
        return;
      }
      const { data: pub } = sb.storage.from("attachments").getPublicUrl(path);
      onSave(pub.publicUrl);
    } finally {
      setSaving(false);
    }
  }

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4"
          onClick={onCancel}
        >
          <motion.div
            initial={{ scale: 0.97, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.97, opacity: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 32 }}
            onClick={(e) => e.stopPropagation()}
            className="rounded-lg border border-[var(--border-strong)] bg-[var(--surface-elevated)] shadow-2xl flex flex-col max-w-[min(95vw,1200px)] max-h-[95vh]"
          >
            {/* Header */}
            <div className="flex items-center gap-2 px-4 h-11 border-b border-[var(--border)]">
              <span className="text-[12.5px] font-semibold">Annotate image</span>
              <span className="text-[10.5px] text-[var(--fg-muted)]">
                {shapes.length} {shapes.length === 1 ? "shape" : "shapes"}
              </span>
              <div className="ml-auto flex items-center gap-1">
                <button
                  onClick={undo}
                  disabled={shapes.length === 0}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[12px] text-[var(--fg-secondary)] hover:bg-[var(--bg-hover)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  title="Undo last shape"
                >
                  <Undo2 size={13} />
                  Undo
                </button>
                <button
                  onClick={save}
                  disabled={saving || shapes.length === 0}
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white text-[12px] font-medium disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                  Save
                </button>
                <button
                  onClick={onCancel}
                  className="w-7 h-7 rounded-md hover:bg-[var(--bg-hover)] flex items-center justify-center text-[var(--fg-tertiary)]"
                  title="Cancel (Esc)"
                >
                  <X size={14} />
                </button>
              </div>
            </div>

            {/* Toolbar */}
            <div className="px-4 h-11 border-b border-[var(--border)] flex items-center gap-3">
              <div className="flex items-center rounded-md border border-[var(--border)] overflow-hidden">
                <ToolButton active={tool === "arrow"} onClick={() => setTool("arrow")} title="Arrow">
                  <ArrowIcon size={13} />
                </ToolButton>
                <ToolButton active={tool === "box"} onClick={() => setTool("box")} title="Box">
                  <Square size={13} />
                </ToolButton>
                <ToolButton active={tool === "pen"} onClick={() => setTool("pen")} title="Pen (freehand)">
                  <Pen size={13} />
                </ToolButton>
                <ToolButton active={tool === "text"} onClick={() => setTool("text")} title="Text">
                  <TypeIcon size={13} />
                </ToolButton>
              </div>
              <div className="flex items-center gap-1">
                {COLORS.map((c) => (
                  <button
                    key={c}
                    onClick={() => setColor(c)}
                    className={cn(
                      "w-5 h-5 rounded-full border-2 transition-transform",
                      color === c
                        ? "border-[var(--fg)] scale-110"
                        : "border-transparent hover:scale-105"
                    )}
                    style={{ background: c }}
                    title={c}
                  />
                ))}
              </div>
              <div className="flex items-center rounded-md border border-[var(--border)] overflow-hidden">
                {([2, 4, 6] as StrokeWidth[]).map((w) => (
                  <button
                    key={w}
                    onClick={() => setWidth(w)}
                    className={cn(
                      "px-2 py-1 text-[11px] transition-colors",
                      width === w
                        ? "bg-[var(--bg-active)] text-[var(--fg)]"
                        : "text-[var(--fg-tertiary)] hover:bg-[var(--bg-hover)]"
                    )}
                    title={`Stroke ${w}px`}
                  >
                    {w}px
                  </button>
                ))}
              </div>
            </div>

            {/* Canvas area */}
            <div className="flex-1 min-h-0 overflow-auto bg-[var(--bg)] p-4 flex items-start justify-center scrollbar-thin">
              <div className="relative inline-block">
                {!imageReady && (
                  <div className="absolute inset-0 flex items-center justify-center text-[12px] text-[var(--fg-muted)]">
                    Loading image…
                  </div>
                )}
                <canvas
                  ref={canvasRef}
                  onPointerDown={onPointerDown}
                  onPointerMove={onPointerMove}
                  onPointerUp={onPointerUp}
                  className="max-w-full h-auto cursor-crosshair touch-none"
                  style={{ maxWidth: "100%", height: "auto" }}
                />
                {textInput && (
                  <input
                    autoFocus
                    value={textInput.value}
                    onChange={(e) => setTextInput({ ...textInput, value: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") commitText();
                      if (e.key === "Escape") setTextInput(null);
                    }}
                    onBlur={commitText}
                    className="absolute bg-[var(--bg-tertiary)] border border-[var(--accent)] rounded px-1 py-0.5 text-[12px] outline-none"
                    style={{
                      left: `${
                        canvasRef.current
                          ? (textInput.at.x / canvasRef.current.width) *
                            canvasRef.current.getBoundingClientRect().width
                          : 0
                      }px`,
                      top: `${
                        canvasRef.current
                          ? (textInput.at.y / canvasRef.current.height) *
                            canvasRef.current.getBoundingClientRect().height
                          : 0
                      }px`,
                      transform: "translateY(-2px)",
                    }}
                  />
                )}
              </div>
            </div>

            {/* Footer hint */}
            <div className="px-4 h-8 border-t border-[var(--border)] flex items-center text-[10.5px] text-[var(--fg-muted)]">
              {tool === "text"
                ? "Click on the image to place text — Enter to commit, Esc to cancel."
                : "Click and drag to draw. Save uploads a new image; the original stays in storage."}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}

function ToolButton({
  active,
  onClick,
  title,
  children,
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "px-2 py-1 transition-colors",
        active ? "bg-[var(--bg-active)] text-[var(--fg)]" : "text-[var(--fg-tertiary)] hover:bg-[var(--bg-hover)]"
      )}
      title={title}
    >
      {children}
    </button>
  );
}
